import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, CheckCircle, Hash, User, Package } from 'lucide-react';
import { RaceSelector, PageShell } from '@/components/shared';
import { BibPickupManagerViewProps } from './bibpickupmanager.props';

const BibPickupManagerView: React.FC<BibPickupManagerViewProps> = ({
  races,
  selectedRaceId,
  setSelectedRaceId,
  searchQuery,
  setSearchQuery,
  searchInputRef,
  loading,
  filteredRegistrations,
  raceTypes,
  pickedUpCount,
  totalCount,
  handleBibPickup,
  isPending,
  embedded,
}) => {
  return (
    <PageShell>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Преземање стартни броеви
          </CardTitle>
          <CardDescription>
            Пребарајте по број или име за да потврдите дека учесникот го презел
            стартниот број.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!embedded && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-1">
                Настан
              </label>
              <RaceSelector
                races={races}
                value={selectedRaceId}
                onChange={setSelectedRaceId}
              />
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Пребарујте по стартен број или име..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 text-lg rounded-xl border-2 border-border focus:border-primary"
              autoFocus
            />
          </div>

          {searchQuery.trim() && (
            <div className="mt-4 border rounded-xl overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground">Вчитување...</div>
              ) : filteredRegistrations.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Search className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-lg font-medium">Нема резултати</p>
                  <p className="text-sm">Обидете се со друг број или име.</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredRegistrations.map((reg) => (
                    <div
                      key={reg.id}
                      className={`flex items-center justify-between p-4 sm:p-5 transition-colors ${
                        reg.bibPickedUp ? 'bg-green-50 dark:bg-green-950/30' : 'hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`flex-shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center font-bold ${
                            reg.bibPickedUp ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          <Hash className="w-3 h-3 mb-0.5" />
                          <span className="text-xl leading-none">{reg.bibNumber}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-foreground text-lg">
                            {reg.firstName} {reg.lastName}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
                            {raceTypes[reg.raceTypeId] && <span>{raceTypes[reg.raceTypeId]}</span>}
                            {reg.city && <span>• {reg.city}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        {reg.bibPickedUp ? (
                          <div className="flex items-center gap-2 text-green-600 font-medium px-4 py-2">
                            <CheckCircle className="w-5 h-5" />
                            <span className="hidden sm:inline">Преземено</span>
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleBibPickup(reg)}
                            disabled={isPending}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 h-12 text-base"
                          >
                            {isPending ? (
                              <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                ...
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5" />
                                Потврди
                              </span>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!searchQuery.trim() && !loading && (
            <div className="mt-4 p-8 text-center text-muted-foreground">
              <User className="w-12 h-12 mx-auto mb-3" />
              <p className="text-lg font-medium">Внесете број или име</p>
              <p className="text-sm">Резултатите ќе се прикажат автоматски.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-primary">{totalCount}</p>
              <p className="text-sm text-primary/80">Вкупно потврдени</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{pickedUpCount}</p>
              <p className="text-sm text-green-600">Преземале број</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default BibPickupManagerView;
